import { useEffect, useRef } from "react";
import { Spinner } from "../../../../components/ui";
import {
  formatArtistNames,
  formatEditionLabel,
} from "../instanceGalleryNavigation";
import {
  joinLines,
  formatSaleAmount,
  formatDate,
  formatValuePrice,
} from "./utils";

const FormattedText = ({ text, className }) => {
  if (typeof text !== "string" || !text.includes("\n")) {
    return <p className={className}>{text}</p>;
  }
  return (
    <div className={className}>
      {text.split("\n").map((line, index) =>
        line.trim() === "" ? (
          <br key={index} />
        ) : (
          <p key={index} className="mb-2">
            {line}
          </p>
        ),
      )}
    </div>
  );
};

const DetailRow = ({ label, value, darkMode, formatted = false }) => {
  if (!value) return null;
  return (
    <div className="mb-4">
      <span
        className={`font-weight-light font-beausite-detail text-xs uppercase tracking-wider ${darkMode ? "text-gray-500" : "text-gray-400"}`}
      >
        {label}
      </span>
      {formatted ? (
        <FormattedText
          text={value}
          className={`font-beausite-text text-base ${darkMode ? "text-gray-100" : "text-gray-900"}`}
        />
      ) : (
        <p
          className={`font-beausite-text text-base ${darkMode ? "text-gray-100" : "text-gray-900"}`}
        >
          {value}
        </p>
      )}
    </div>
  );
};

const SaleHistoryTable = ({ sales, darkMode }) => {
  if (!sales || sales.length === 0) return null;

  return (
    <div className="mt-8">
      <h2
        className={`font-weight-light mb-3 text-xs uppercase tracking-wider ${darkMode ? "text-gray-500" : "text-gray-400"}`}
      >
        Sale History
      </h2>
      <table className="w-full text-sm">
        <thead>
          <tr
            className={`border-b text-left text-xs uppercase tracking-wider ${darkMode ? "border-gray-700 text-gray-500" : "border-gray-200 text-gray-400"}`}
          >
            <th className="pb-2 pr-4 font-normal">Client</th>
            <th className="pb-2 pr-4 font-normal">Invoice</th>
            <th className="pb-2 pr-4 font-normal">Amount</th>
            <th className="pb-2 font-normal">Date</th>
          </tr>
        </thead>
        <tbody className="font-beausite-text">
          {sales.map(item => (
            <tr
              key={item.id}
              className={`border-b ${darkMode ? "border-gray-800" : "border-gray-100"}`}
            >
              <td
                className={`py-2 pr-4 ${darkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                {item.invoice?.companyName || "—"}
              </td>
              <td
                className={`py-2 pr-4 ${darkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                {item.invoice?.reference || "—"}
              </td>
              <td
                className={`py-2 pr-4 ${darkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                {formatSaleAmount(item)}
              </td>
              <td
                className={`py-2 ${darkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                {formatDate(item.date)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DetailPane = ({ instance, sales, loading, darkMode }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [instance?.id]);

  if (loading) {
    return (
      <div className={`h-full ${darkMode ? "bg-neutral-950" : ""}`}>
        <div className="flex h-full items-center justify-center">
          <Spinner className={darkMode ? "text-white" : "text-black"} />
        </div>
      </div>
    );
  }

  if (!instance) {
    return (
      <div className={`h-full ${darkMode ? "bg-neutral-950" : ""}`}>
        <div
          className={`flex h-full items-center justify-center ${darkMode ? "text-gray-500" : "text-gray-400"}`}
        >
          No instance selected
        </div>
      </div>
    );
  }

  const artwork = instance.edition?.artwork;
  const edition = instance.edition;
  const editionLabel = formatEditionLabel({
    editionNumber: instance.editionNumber,
    editionTotal: edition?.number,
  });

  return (
    <div className={`h-full ${darkMode ? "bg-neutral-950" : ""}`}>
      <div
        ref={scrollRef}
        className="h-full overflow-y-auto px-6 pb-6 pt-6 landscape:px-8 landscape:pb-8 landscape:pt-16"
      >
        <div>
          <h1
            className={`font-beausite-text text-4xl font-normal leading-tight ${darkMode ? "text-white" : "text-black"}`}
            style={{ textIndent: "-2px" }}
          >
            {artwork?.title}
          </h1>
          {formatArtistNames(artwork?.artists) && (
            <p className="font-beausite-text text-xl text-gray-500">
              {formatArtistNames(artwork?.artists)}
            </p>
          )}

          <div className="mt-8">
            <div className="grid-fill-last mb-4 grid auto-rows-min grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3">
              <DetailRow
                label="Reference"
                value={instance.name}
                darkMode={darkMode}
              />
              <DetailRow
                label="Year"
                value={edition?.year}
                darkMode={darkMode}
              />
              {editionLabel && (
                <DetailRow
                  label="Edition"
                  value={editionLabel}
                  darkMode={darkMode}
                />
              )}
              <DetailRow
                label="Medium"
                value={artwork?.medium}
                darkMode={darkMode}
              />
              <DetailRow
                label="Status"
                value={instance.status?.name}
                darkMode={darkMode}
              />
              <DetailRow
                label="Dimensions"
                value={artwork?.dimensions}
                darkMode={darkMode}
              />
              <DetailRow
                label="Sale Price"
                value={formatValuePrice(instance.value)}
                darkMode={darkMode}
              />
              <DetailRow
                label="Consignment Price"
                value={formatValuePrice(instance.consignmentValue)}
                darkMode={darkMode}
              />
            </div>
            <DetailRow
              label="Precis"
              value={artwork?.precis}
              darkMode={darkMode}
              formatted={true}
            />
            <DetailRow
              label="Artwork Description"
              value={joinLines(artwork?.description)}
              darkMode={darkMode}
              formatted={true}
            />
            <DetailRow
              label="Instance Description"
              value={joinLines(instance?.description)}
              darkMode={darkMode}
            />
          </div>

          <SaleHistoryTable sales={sales} darkMode={darkMode} />
        </div>
      </div>
    </div>
  );
};

export default DetailPane;
